import { createContext } from 'react';
import { ReactSDKClient } from '@optimizely/react-sdk';
import { ParsedUrlQuery } from 'querystring';
import fromPairs from 'lodash/fromPairs';
import { logWarn } from '@zoopla/logger';

export type OptimizelyOverrides = {
    experiments: OverrideState['experiments']['force'];
    features: OverrideState['features']['force'];
};

export const forceExperiments = (
    optimizelyVariations: OptimizelyOverrides['experiments'] | undefined,
    userId: string | undefined,
    optimizelyClient: ReactSDKClient
): void => {
    if (optimizelyVariations && typeof userId === 'string')
        Object.entries(optimizelyVariations).forEach(
            ([experiment, variation]) => {
                optimizelyClient.setForcedVariation(
                    experiment,
                    userId,
                    variation
                );
            }
        );
};

export const getOptimizelyOverrides = (
    cookies: Record<string, string | undefined>,
    query: ParsedUrlQuery
): OptimizelyOverrides => {
    const cookieOverrides = getOverridesFromCookies(cookies);
    const queryOverrides = getOverridesFromQuery(query);

    const forcedOverrides = reconcileOverrideStates(
        cookieOverrides,
        queryOverrides
    );

    return forcedOverrides;
};

export const writeOverridesToCookies = (
    overrides: OptimizelyOverrides
): void => {
    if (typeof window === 'undefined') return;

    const newForcedExperimentsCookie = stringifyState(overrides.experiments);

    const newForcedFeaturesCookie = stringifyState(overrides.features);

    document.cookie = `${FORCE_FEATURES_COOKIE_NAME}=${newForcedFeaturesCookie};path=/`;
    document.cookie = `${FORCE_EXPERIMENTS_COOKIE_NAME}=${newForcedExperimentsCookie};path=/`;
};

const FORCE_EXPERIMENT_QUERY_PARAM_KEY_PREFIX = 'force_test_variation_';
const CANCEL_EXPERIMENT_QUERY_PARAM_KEY_PREFIX = 'cancel_force_test_variation_';
const FORCE_FEATURE_QUERY_PARAM_KEY_PREFIX = 'force_feature_flag_';
const CANCEL_FEATURE_QUERY_PARAM_KEY_PREFIX = 'cancel_force_feature_flag_';

const FORCE_EXPERIMENTS_COOKIE_NAME = 'forced_experiments';
const FORCE_FEATURES_COOKIE_NAME = 'forced_features';

type OverrideState = {
    experiments: {
        force: {
            [experiment: string]: string;
        };
        cancel: string[];
    };
    features: {
        force: { [feature: string]: boolean };
        cancel: string[];
    };
};

const getOverridesFromQuery = (query: ParsedUrlQuery): OverrideState => {
    const entries = Object.entries(query);

    return {
        experiments: {
            force: fromPairs(
                entries
                    .filter(
                        byKeyPrefix(FORCE_EXPERIMENT_QUERY_PARAM_KEY_PREFIX)
                    )
                    .map(
                        removeKeyPrefix(FORCE_EXPERIMENT_QUERY_PARAM_KEY_PREFIX)
                    )
                    .map(useFirstValue)
            ),
            cancel: entries
                .filter(byKeyPrefix(CANCEL_EXPERIMENT_QUERY_PARAM_KEY_PREFIX))
                .map(removeKeyPrefix(CANCEL_EXPERIMENT_QUERY_PARAM_KEY_PREFIX))
                .map(extractName)
        },
        features: {
            force: fromPairs(
                entries
                    .filter(byKeyPrefix(FORCE_FEATURE_QUERY_PARAM_KEY_PREFIX))
                    .map(removeKeyPrefix(FORCE_FEATURE_QUERY_PARAM_KEY_PREFIX))
                    .map(useFirstValue)
                    .map(([name, value]) => [name, isValueTrue(value)])
            ),
            cancel: entries
                .filter(byKeyPrefix(CANCEL_FEATURE_QUERY_PARAM_KEY_PREFIX))
                .map(removeKeyPrefix(CANCEL_FEATURE_QUERY_PARAM_KEY_PREFIX))
                .map(extractName)
        }
    };
};

const mapForcedCookiesToBooleans = (
    cookie: Record<string, string | undefined>
) =>
    Object.keys(cookie).reduce((acc, key) => {
        const value = cookie[key] || '';

        return {
            ...acc,
            [key]: isValueTrue(value)
        };
    }, {});

const getForcedExperimentsFromCookie = (
    cookie: string
): OverrideState['experiments']['force'] => {
    try {
        const experimentsToForce = JSON.parse(cookie);

        return experimentsToForce;
    } catch (error) {
        logWarn('Failed to parse experiment override cookie', error);
        return {};
    }
};

const getForcedFeaturesFromCookie = (
    cookie: string
): OverrideState['features']['force'] => {
    try {
        const featuresToForceCookies = JSON.parse(cookie);
        const featuresToForce = mapForcedCookiesToBooleans(
            featuresToForceCookies
        );

        return featuresToForce;
    } catch (error) {
        logWarn('Failed to parse feature override cookie', error);
        return {};
    }
};

const getOverridesFromCookies = (
    cookies: Record<string, string | undefined>
): OverrideState => {
    const forcedExperimentsCookie = cookies[FORCE_EXPERIMENTS_COOKIE_NAME];
    const forcedFeaturesCookie = cookies[FORCE_FEATURES_COOKIE_NAME];

    const forcedExperiments = forcedExperimentsCookie
        ? getForcedExperimentsFromCookie(forcedExperimentsCookie)
        : {};

    const forcedFeatures = forcedFeaturesCookie
        ? getForcedFeaturesFromCookie(forcedFeaturesCookie)
        : {};

    return {
        experiments: {
            force: forcedExperiments,
            cancel: []
        },
        features: {
            force: forcedFeatures,
            cancel: []
        }
    };
};

const reconcileOverrideStates = (
    cookieState: OverrideState,
    queryState: OverrideState
): OptimizelyOverrides => {
    const totalForcedFeatures = {
        ...cookieState.features.force,
        ...queryState.features.force
    };

    const reconciledForcedFeatures = omitByKey(
        totalForcedFeatures,
        queryState.features.cancel
    );

    const totalForcedExperiments = {
        ...cookieState.experiments.force,
        ...queryState.experiments.force
    };

    const reconciledForcedExperiments = omitByKey(
        totalForcedExperiments,
        queryState.experiments.cancel
    );

    return {
        experiments: reconciledForcedExperiments,
        features: reconciledForcedFeatures
    };
};

export const OptimizelyOverridesContext = createContext<
    OptimizelyOverrides | undefined
>({
    experiments: {},
    features: {}
});

const stringifyState = (
    state: OptimizelyOverrides['experiments'] | OptimizelyOverrides['features']
): string => {
    try {
        return JSON.stringify(state);
    } catch (error) {
        logWarn(
            'Failed to persist optimizely override state to a cookie',
            error
        );
        return '';
    }
};

type Entry = [string, string | string[] | undefined];

const byKeyPrefix: (keyPrefix: string) => (entry: Entry) => boolean =
    (keyPrefix) =>
    ([key]) =>
        key.startsWith(keyPrefix);

const removeKeyPrefix: (keyPrefix: string) => (entry: Entry) => Entry =
    (keyPrefix) =>
    ([key, value]) =>
        [key.replace(keyPrefix, ''), value];

const useFirstValue: (entry: Entry) => [string, string] = ([key, value]) => [
    key,
    typeof value === 'string' ? value : value?.[0] ?? ''
];

const extractName: (entry: [string, ...unknown[]]) => string = ([name]) => name;

const isValueTrue = (value: string): boolean =>
    value !== 'false' && value !== '0';

const omitByKey = <T>(
    obj: Record<string, T>,
    keysToRemove: string[]
): Record<string, T> =>
    fromPairs(
        Object.entries(obj).filter(([key]) => !keysToRemove.includes(key))
    );
